const AccordionItem = ({ indexLabel, title, desc, tag, defaultOpen }) => (
  <details
    open={defaultOpen}
    className="group border-b border-[#DFE6F7] [&::-webkit-details-marker]:hidden"
  >
    <summary className="cursor-pointer list-none flex items-center gap-3 py-5">
      <span className="text-primary text-sm font-extrabold w-18 flex-none">
        {indexLabel}
      </span>
      <strong className="text-[#1E2027] group-open:text-primary text-base md:text-lg flex-1 min-w-0">
        {title}
      </strong>
      <b
        aria-hidden="true"
        className="text-primary text-2xl font-normal transition-transform group-open:rotate-45 w-6 flex-none text-right"
      >
        +
      </b>
    </summary>
    <div className="pb-6 md:pl-[56px] md:pr-12">
      {desc && (
        <p className="text-[#565656] text-sm md:text-base leading-relaxed mb-2">
          {desc}
        </p>
      )}
      {tag && <small className="text-primary text-sm font-bold">{tag}</small>}
    </div>
  </details>
);

const PhaseAccordion = ({ phase, indexLabel, defaultOpen }) => (
  <details
    open={defaultOpen}
    className="group border-b border-[#DFE6F7] [&::-webkit-details-marker]:hidden"
  >
    <summary className="cursor-pointer list-none grid grid-cols-[36px_1fr_24px] md:grid-cols-[44px_1fr_24px] items-center gap-3 md:gap-4 py-7">
      <div className="flex gap-5 justify-between items-center">
        <div className="flex gap-5">
          <span className="text-primary text-sm font-poppins font-extrabold">
            {indexLabel}
          </span>
          <span className="flex flex-col gap-0.5 min-w-0">
            <small className="text-[#565656] text-xs md:text-base font-semibold">
              {phase.phaseWeeks}
            </small>
            <strong className="text-[#1E2027] group-open:text-primary text-base md:text-lg">
              {phase.phaseTitle}
            </strong>
          </span>
        </div>
        <b
          aria-hidden="true"
          className="text-primary text-2xl font-normal transition-transform group-open:rotate-45 text-right"
        >
          +
        </b>
      </div>
    </summary>
    <div className="pb-6 pl-9 pr-3 md:pl-[56px] md:pr-12">
      {phase.phaseDescription && (
        <p className="text-[#565656] text-sm md:text-base leading-relaxed max-w-[720px] mb-3.5">
          {phase.phaseDescription}
        </p>
      )}
      <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-9">
        {phase.items.map((item) => (
          <li key={item.weekLabel} className="text-[#1E2027] text-sm mb-1.5">
            {item.title}
          </li>
        ))}
      </ul>
    </div>
  </details>
);

const PhasedCurriculum = ({ phases }) => (
  <div className="border-t border-[#DFE6F7]">
    {phases.map((phase, phaseIndex) => (
      <PhaseAccordion
        key={phase.phaseTitle}
        phase={phase}
        indexLabel={String(phaseIndex + 1).padStart(2, "0")}
        defaultOpen={phaseIndex === 0}
      />
    ))}
  </div>
);

const FlatCurriculum = ({ outlines }) => (
  <div className="border-t border-[#DFE6F7]">
    {outlines.map((outline, i) => (
      <AccordionItem
        key={outline.unitId}
        indexLabel={String(outline.unitId).padStart(2, "0")}
        title={outline.unitTitle}
        desc={outline.unitDescription}
        tag={outline.unitTag}
        defaultOpen={i === 0}
      />
    ))}
  </div>
);

const CourseWhatYouWillLearnSection = ({ selectedCourseData }) => {
  const phases = selectedCourseData?.curriculumPhases;
  const outlines = selectedCourseData?.courseOutlines;

  const unitCount = phases
    ? phases.reduce((sum, phase) => sum + phase.items.length, 0)
    : outlines?.length || 0;

  if (!phases?.length && !outlines?.length) return null;

  return (
    <section id="curriculum" className="bg-white">
      <div className="border border-[#DFE6F7] rounded-[26px] bg-white shadow-course-card mb-8 p-6 md:p-9">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-2">
          <h2 className="text-primary font-bold text-3xl md:text-5xl tracking-tight m-0">
            {selectedCourseData?.curriculumHeading || "Curriculum"}
          </h2>
          <div className="flex flex-wrap gap-2">
            <span className="bg-[#F0F5FF] text-primary rounded-full px-3 py-1.5 text-[14px] font-semibold">
              {unitCount} Units
            </span>
            <span className="bg-[#F0F5FF] text-primary rounded-full px-3 py-1.5 text-[14px] font-semibold">
              {selectedCourseData?.duration}
            </span>
            <span className="bg-[#F0F5FF] text-primary rounded-full px-3 py-1.5 text-[14px] font-semibold">
              Live + Self-paced
            </span>
          </div>
        </div>

        <div className="mt-6">
          {phases?.length > 0 ? (
            <PhasedCurriculum phases={phases} />
          ) : (
            <FlatCurriculum outlines={outlines} />
          )}
        </div>
      </div>
    </section>
  );
};

export default CourseWhatYouWillLearnSection;
